/**
 * Announcing a call out loud.
 *
 * The board is the announcement. Speech is what makes somebody look up at it, and
 * a hall with the volume down must show exactly what a hall with sound shows — so
 * every failure in here is swallowed and nothing in here is ever awaited by the
 * code that draws the screen.
 *
 * What the field does, and why this follows it
 * --------------------------------------------
 * Qmatic, Wavetec and Nemo-Q all announce from **pre-recorded fragments** — a
 * professional reading of "ticket", of each digit, of each desk number — stitched
 * together at the moment of the call. Not text-to-speech. That is not conservatism:
 * a fragment pack works with the branch's uplink down, pronounces the bank's own
 * words the way the bank says them, and can be re-recorded by the customer's own
 * voice talent, which banks ask for.
 *
 * It matters twice over for Azerbaijani, where there is effectively no offline
 * text-to-speech voice to fall back on.
 *
 * So `Source` is the seam. `speechSource` is what ships today and is honest about
 * what it is: the browser's own voice, which every Windows and macOS machine has
 * for English and which many have for nothing else. `fragmentSource` is where a
 * recorded pack will go, and the queue, the chime, the repeat and the phrase
 * grammar below are all written so that swapping the source changes nothing else.
 */

import type { VoiceSettings } from './types'

export type { VoiceSettings }

/**
 * Whether this machine is the one that reads the calls out.
 *
 * The default follows the room rather than the kind of machine: a hall board
 * exists to be looked at when a number is called, and a kiosk is a machine
 * somebody is already standing at. A branch whose television is a panel with no
 * audio turns it on for the kiosk and off for the board — and a branch with sound
 * on both would hear every number twice, a fifth of a second apart, which is worse
 * than hearing it once.
 */
export function readsCalls(v: VoiceSettings | undefined, kind: CallKind): boolean {
  if (v?.announce === false) return false
  if (v?.calls !== undefined) return v.calls
  return kind === 'display'
}

/** Whether a call plays the chime here, with or without the words. Unset follows
 *  readsCalls, so a machine saved before the two were apart sounds as it did. */
export function chimesCalls(v: VoiceSettings | undefined, kind: CallKind): boolean {
  if (v?.announce === false) return false
  if (v?.chime !== undefined) return v.chime
  return readsCalls(v, kind)
}

/** The settings a call is announced with on this machine (calls and chime
 *  resolved), or null when a call makes no sound here. */
export function callVoice(v: VoiceSettings | undefined, kind: CallKind): VoiceSettings | null {
  const calls = readsCalls(v, kind)
  const chime = chimesCalls(v, kind)
  if (!calls && !chime) return null
  return { ...v, calls, chime }
}

/** Whether a call makes any sound on this machine: chime, words or both. */
export function announcesCalls(v: VoiceSettings | undefined, kind: CallKind): boolean {
  return callVoice(v, kind) !== null
}

export type CallKind = 'kiosk' | 'display' | 'desk_display'

/** The longest a chime sound plays; a longer upload is cut off here. */
export const MAX_CHIME_SECONDS = 10
/** The largest chime sound the server takes (internal/api/sounds.go). */
export const MAX_SOUND_BYTES = 2 << 20

/** A sound file's length in seconds as this browser reads it, or null when it cannot. */
export function soundSeconds(file: Blob): Promise<number | null> {
  return new Promise((resolve) => {
    let url = ''
    try {
      url = URL.createObjectURL(file)
    } catch {
      resolve(null)
      return
    }
    const audio = new Audio()
    const done = (n: number | null) => {
      URL.revokeObjectURL(url)
      resolve(n)
    }
    audio.preload = 'metadata'
    audio.onloadedmetadata = () => done(Number.isFinite(audio.duration) ? audio.duration : null)
    audio.onerror = () => done(null)
    setTimeout(() => done(null), 5000)
    audio.src = url
  })
}

/** One thing to say. Kept as parts rather than a sentence so a fragment pack can
 * read each part from its own recording without parsing English back apart. */
export type Announcement = {
  /** The ticket as printed: "A-042". */
  number: string
  /** The desk as the customer reads it on the board: "Counter 3". Empty when the
   *  announcement is a kiosk telling the person standing at it what they were
   *  given, which names no desk because none has been chosen yet. */
  desk?: string
}

/** Where the sound comes from. */
export type Source = {
  /** Say one announcement, resolving when it has finished speaking. */
  say(a: Announcement, language: string): Promise<void>
  /** Whether this source can say anything at all in this language. */
  available(language: string): boolean
}

// How many announcers are sounding a call now; a playlist film lowers its sound meanwhile.
let sounding = 0
const soundingListeners = new Set<() => void>()

/** Whether a call is being chimed or read out on this screen. */
export function callSounding(): boolean {
  return sounding > 0
}

/** Told whenever callSounding changes; returns the unsubscribe. */
export function onCallSounding(listener: () => void): () => void {
  soundingListeners.add(listener)
  return () => soundingListeners.delete(listener)
}

function setSounding(delta: number): void {
  const was = sounding > 0
  sounding = Math.max(0, sounding + delta)
  if (was !== sounding > 0) soundingListeners.forEach((l) => l())
}

const DEFAULT_REPEAT = 2
const GAP_BETWEEN_REPEATS_MS = 600

/**
 * The announcer.
 *
 * One per screen, and the whole reason it exists is the queue. Two desks calling
 * within the same second is ordinary in a busy branch, and the obvious
 * implementation — speak, cancelling whatever is in progress — means the first
 * customer's number is cut off halfway and never said again. They are still sitting
 * down. The board moved on without them.
 *
 * So announcements are queued and said one after another, and nothing ever cancels
 * anything. The cost is that a burst of calls takes a few seconds to read out,
 * which is correct: that is how long it takes to say them.
 */
export class Announcer {
  private queue: Queued[] = []
  private speaking = false
  private audio: AudioContext | null = null
  // Decoded chime sounds by address; a failed load is dropped so the next call tries again.
  private sounds = new Map<string, Promise<AudioBuffer | null>>()

  /** Set when the browser refused to make a sound until somebody interacts with
   * the page. An unattended hall board is never touched, so this is the failure
   * that actually happens in the field — and the screen has to be able to say so. */
  blocked = false

  constructor(
    private source: Source,
    private onBlockedChange: (blocked: boolean) => void = () => {},
    /** Where an uploaded chime sound is fetched from; without it the built-in tone plays. */
    private soundURL?: (assetId: string) => string,
  ) {}

  /**
   * A dropped call is worse than a late one, so this never rejects and never throws.
   * `calls: false` plays the chime alone; `chime: false` says the words alone.
   */
  announce(a: Announcement, settings: VoiceSettings): void {
    if (settings.announce === false) return
    const language = settings.language || 'en'
    // No voice for the language (often Azerbaijani) still rings the chime.
    if (!this.speaks(settings, language) && settings.chime === false) return

    // A queue that has grown past a few entries means the branch is calling faster
    // than anybody can listen. Reading all of it out would have the board still
    // announcing numbers that were served minutes ago, so the oldest are dropped —
    // the board still shows them, which is the actual announcement.
    this.queue.push({ a, settings, language })
    if (this.queue.length > 4) this.queue.splice(0, this.queue.length - 4)

    void this.drain()
  }

  private speaks(settings: VoiceSettings, language: string): boolean {
    return settings.calls !== false && this.source.available(language)
  }

  private async drain(): Promise<void> {
    if (this.speaking) return
    this.speaking = true
    setSounding(1)
    try {
      while (this.queue.length > 0) {
        const next = this.queue.shift()
        if (!next) break
        const { a, settings, language } = next
        const times = clampRepeat(settings.repeat)
        for (let i = 0; i < times; i++) {
          if (i === 0 && settings.chime !== false) await this.chime(settings.chimeAsset)
          if (!this.speaks(settings, language)) break
          if (i > 0) await wait(GAP_BETWEEN_REPEATS_MS)
          await this.source.say(a, language)
        }
      }
    } catch {
      // A hall with no sound is a hall with a working board.
    } finally {
      this.speaking = false
      setSounding(-1)
    }
  }

  /**
   * Two descending tones, synthesised rather than played from a file.
   *
   * A file would be one more thing to ship, to cache, and to get wrong on a
   * machine with no sound card; two oscillators are four lines and always there.
   * Descending rather than ascending because a rising pair reads as a question and
   * a falling pair reads as an announcement — which is the one every public address
   * system in an airport uses.
   */
  private async chime(assetId?: string): Promise<void> {
    try {
      const ctx = this.context()
      if (!ctx) return
      if (ctx.state === 'suspended') {
        await ctx.resume().catch(() => undefined)
      }
      if (ctx.state !== 'running') {
        this.setBlocked(true)
        return
      }
      this.setBlocked(false)

      // An uploaded sound that will not load or decode rings the built-in tone instead.
      const own = assetId ? await this.sound(ctx, assetId) : null
      if (own) {
        const src = ctx.createBufferSource()
        src.buffer = own
        src.connect(ctx.destination)
        const seconds = Math.min(own.duration, MAX_CHIME_SECONDS)
        src.start()
        src.stop(ctx.currentTime + seconds)
        await wait(seconds * 1000 + 100)
        return
      }

      const now = ctx.currentTime
      for (const [i, hz] of [880, 660].entries()) {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.value = hz
        // Ramped rather than switched, because a square edge on a hall speaker is
        // a click before the tone and the click is what people hear.
        const at = now + i * 0.18
        gain.gain.setValueAtTime(0, at)
        gain.gain.linearRampToValueAtTime(0.25, at + 0.02)
        gain.gain.linearRampToValueAtTime(0, at + 0.17)
        osc.connect(gain).connect(ctx.destination)
        osc.start(at)
        osc.stop(at + 0.18)
      }
      await wait(420)
    } catch {
      // No tone is not a reason to skip the words.
    }
  }

  private sound(ctx: AudioContext, assetId: string): Promise<AudioBuffer | null> {
    const url = this.soundURL?.(assetId)
    if (!url) return Promise.resolve(null)
    let loading = this.sounds.get(url)
    if (!loading) {
      const load = fetch(url)
        .then((r) => {
          if (!r.ok) throw new Error(String(r.status))
          return r.arrayBuffer()
        })
        .then((bytes) => decode(ctx, bytes))
      // A server that never answers must not hold the call: the tone rings instead.
      loading = Promise.race([load, wait(SOUND_WAIT_MS).then(() => Promise.reject(new Error('slow')))]).catch(() => {
        this.sounds.delete(url)
        return null
      })
      this.sounds.set(url, loading)
    }
    return loading
  }

  /** Play the chime alone, from a press, so an administrator hears it. */
  previewChime(settings: VoiceSettings): void {
    void this.unblock()
    this.announce({ number: 'A-042' }, { ...settings, announce: true, calls: false, chime: true })
  }

  private context(): AudioContext | null {
    if (this.audio) return this.audio
    try {
      if (typeof window === 'undefined') return null
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!Ctor) return null
      this.audio = new Ctor()
      return this.audio
    } catch {
      return null
    }
  }

  private setBlocked(v: boolean): void {
    if (this.blocked === v) return
    this.blocked = v
    this.onBlockedChange(v)
  }

  /**
   * Called from a real interaction — a click anywhere on the board.
   *
   * Browsers will not make a sound until the page has been interacted with, and a
   * hall board is never interacted with. So the screen offers one button, once, and
   * this is what it calls.
   */
  async unblock(): Promise<void> {
    const ctx = this.context()
    if (!ctx) return
    await ctx.resume().catch(() => undefined)
    // Speech is held back by the same rule; a silent utterance inside the gesture frees it.
    try {
      window.speechSynthesis?.speak(new SpeechSynthesisUtterance(''))
    } catch {
      // No speech is not a reason to keep the tone off.
    }
    this.setBlocked(ctx.state !== 'running')
  }

  /** Whether sound is allowed yet, asked at start so a silent board says so before the first call. */
  probe(): void {
    const ctx = this.context()
    if (ctx) this.setBlocked(ctx.state !== 'running')
  }

  /** Say something now, to check the hall can hear it. */
  test(settings: VoiceSettings): void {
    this.announce({ number: 'A-042', desk: settings.language === 'az' ? 'Pəncərə 3' : 'Counter 3' }, settings)
  }
}

type Queued = { a: Announcement; settings: VoiceSettings; language: string }

const SOUND_WAIT_MS = 3000

// The callback form: older Safari has no promise from decodeAudioData.
function decode(ctx: AudioContext, bytes: ArrayBuffer): Promise<AudioBuffer> {
  return new Promise((resolve, reject) => {
    const p = ctx.decodeAudioData(bytes, resolve, reject) as Promise<AudioBuffer> | undefined
    p?.catch?.(() => undefined)
  })
}

function clampRepeat(n: number | undefined): number {
  if (!n || n < 1) return DEFAULT_REPEAT
  return Math.min(n, 3)
}

// The bare global, not window.setTimeout. The difference only shows up off a
// browser main thread — but the failure mode is the one that matters: a throw
// anywhere inside the repeat loop is swallowed by the announcer, so the number
// would simply be read once instead of twice and nothing would say why.
function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * The browser's own voice.
 *
 * Honest about what it is: every Windows and macOS machine has an English voice,
 * many have Russian, and almost none have Azerbaijani. `available` is what keeps a
 * board from announcing Azerbaijani words in an English voice, which is worse than
 * silence — a number read in the wrong phonetics is a number nobody recognises as
 * theirs.
 */
export function speechSource(): Source {
  const voices = (): SpeechSynthesisVoice[] => {
    try {
      return window.speechSynthesis?.getVoices() ?? []
    } catch {
      return []
    }
  }

  const voiceFor = (language: string): SpeechSynthesisVoice | undefined =>
    voices().find((v) => v.lang.toLowerCase().startsWith(language.toLowerCase()))

  return {
    available(language) {
      if (typeof window === 'undefined' || !('speechSynthesis' in window)) return false
      // An empty list means the browser has not loaded them yet rather than that
      // it has none, and refusing then would silence the first call after a boot —
      // which on a hall board is every morning's first customer.
      return voices().length === 0 || voiceFor(language) !== undefined
    },

    say(a, language) {
      return new Promise<void>((resolve) => {
        try {
          const utterance = new SpeechSynthesisUtterance(phrase(a, language))
          utterance.lang = bcp47(language)
          const voice = voiceFor(language)
          if (voice) utterance.voice = voice
          // Slower than conversation. A number read at speaking pace across a hall
          // is a number heard by the person already standing at the desk.
          utterance.rate = 0.85
          utterance.onend = () => resolve()
          utterance.onerror = () => resolve()
          // Deliberately no cancel(): see the Announcer. Anything already speaking
          // is somebody else's number and it is going to be finished.
          window.speechSynthesis.speak(utterance)
          // A browser that never fires onend — it happens, on long-suspended tabs —
          // must not wedge the queue for the rest of the day.
          setTimeout(resolve, 8000)
        } catch {
          resolve()
        }
      })
    },
  }
}

/** Map our two-letter code to the tag a speech engine expects. */
function bcp47(language: string): string {
  switch (language) {
    case 'az':
      return 'az-AZ'
    case 'ru':
      return 'ru-RU'
    default:
      return 'en-GB'
  }
}

/**
 * The words, in order.
 *
 * "A-042" read as written becomes "a minus forty-two", which is not the number
 * anybody is holding — so the prefix is spelled and the digits are read one at a
 * time. That is also how the recorded packs are cut, which is why this grammar is
 * here rather than inside the speech source.
 */
export function phrase(a: Announcement, language: string): string {
  const number = spellNumber(a.number)
  if (!a.desk) {
    // A kiosk telling the customer standing at it what they have just been given.
    // No desk, because none has been chosen yet — the board will say that later.
    switch (language) {
      case 'az':
        return `Sizin nömrəniz ${number}`
      case 'ru':
        return `Ваш номер ${number}`
      default:
        return `Your number is ${number}`
    }
  }
  switch (language) {
    case 'az':
      return `${number}, ${a.desk}`
    case 'ru':
      return `Талон ${number}, ${a.desk}`
    default:
      return `Ticket ${number}, please go to ${a.desk}`
  }
}

/** A-042 becomes "A, zero four two". */
export function spellNumber(displayNumber: string): string {
  const [prefix, digits] = displayNumber.split('-')
  if (!digits) return displayNumber.split('').join(' ')
  return `${(prefix ?? '').split('').join(' ')}, ${digits.split('').join(' ')}`
}
